/**
 * AILHealthPage — Platform Admin Only
 * Shows AIL collection health, agent mastery seeding status,
 * and BKT validation metrics from:
 *   GET /ail/bootstrap/stats
 *   GET /ail/bootstrap/validation-metrics
 *
 * Route: /coach/platform/ail-health
 * Access: isAdmin only (enforced in App.js via RequireAuth + role check,
 *         and by the backend _require_admin guard on both endpoints).
 */

import React, { useEffect, useState, useCallback } from 'react';
import CoachLayout from './CoachLayout';
import api from '../../services/api';

// ── colour helpers ─────────────────────────────────────────────────────────

function statusColor(val, good, warn) {
  if (val === -1) return '#475569'; // unavailable
  if (val >= good) return '#4ade80';
  if (val >= warn) return '#fbbf24';
  return '#f87171';
}

function qualityColor(accuracy) {
  if (accuracy >= 0.68) return '#4ade80';
  if (accuracy >= 0.55) return '#fbbf24';
  return '#f87171';
}

function Pill({ label, value, color }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.08)`,
      borderRadius: 10, padding: '14px 18px', minWidth: 120, textAlign: 'center',
    }}>
      <span style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'monospace' }}>{value}</span>
      <span style={{ fontSize: 11, color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>{title}</h3>
      {subtitle && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>{subtitle}</p>}
    </div>
  );
}

// ── Collection row ─────────────────────────────────────────────────────────

function CollectionRow({ name, count, goodAt, warnAt }) {
  const color = statusColor(count, goodAt ?? 1, warnAt ?? 1);
  const label = count === -1 ? 'unavailable' : count === 0 ? 'empty' : count.toLocaleString();
  return (
    <tr>
      <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#94a3b8', paddingRight: 16 }}>{name}</td>
      <td style={{ textAlign: 'right', fontWeight: 700, color, fontFamily: 'monospace', fontSize: 13 }}>{label}</td>
      <td style={{ paddingLeft: 10 }}>
        <span style={{
          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
          background: color, flexShrink: 0,
        }} />
      </td>
    </tr>
  );
}

// ── Metric card ────────────────────────────────────────────────────────────

function MetricCard({ title, value, target, unit = '', note }) {
  const numeric = typeof value === 'number' ? value : null;
  const color = numeric !== null ? qualityColor(numeric) : '#94a3b8';
  const displayVal = numeric !== null ? (unit === '%' ? `${(numeric * 100).toFixed(1)}%` : numeric.toFixed(3)) : '—';
  const displayTarget = target != null ? (unit === '%' ? `${(target * 100).toFixed(0)}%` : target) : null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10, padding: '16px 20px',
    }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: 'monospace' }}>{displayVal}</div>
      {displayTarget && (
        <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
          Target: {displayTarget} {unit}
        </div>
      )}
      {note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>{note}</div>}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function AILHealthPage() {
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchAll = useCallback(() => {
    setLoadingStats(true);
    setLoadingMetrics(true);
    setError(null);

    Promise.allSettled([
      api.get('/ail/bootstrap/stats'),
      api.get('/ail/bootstrap/validation-metrics'),
    ]).then(([statsRes, metricsRes]) => {
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      } else {
        setError(statsRes.reason?.response?.data?.detail || 'Failed to load AIL stats');
      }
      if (metricsRes.status === 'fulfilled') {
        setMetrics(metricsRes.value.data);
      }
      setLastRefreshed(new Date().toLocaleTimeString());
      setLoadingStats(false);
      setLoadingMetrics(false);
    });
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const collections = stats?.collections || {};
  const agentMastery = stats?.agent_mastery_seeded || {};
  const liveAgent = metrics?.live_agent_mastery || {};
  const benchmarks = metrics?.benchmark_datasets || {};
  const targets = metrics?.targets || {};

  const overallHealth = (() => {
    if (!stats) return null;
    const hasItems = (collections.cat_item_bank || 0) > 100;
    const hasAgents = (stats.total_agents_with_mastery || 0) >= 2;
    const hasCards = (collections.fsrs_eval_cards || 0) > 0;
    if (hasItems && hasAgents && hasCards) return { label: 'Healthy', color: '#4ade80' };
    if (hasItems || hasAgents) return { label: 'Partial', color: '#fbbf24' };
    return { label: 'Not Seeded', color: '#f87171' };
  })();

  return (
    <CoachLayout title="AIL Platform Health">
      <div data-testid="ail-health-page">

        {/* Header bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <div>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              Adaptive Intelligence Layer — collection seeding, agent mastery, and BKT model quality.
              Visible to platform admins only.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {lastRefreshed && (
              <span style={{ fontSize: 11, color: '#475569' }}>Last refreshed: {lastRefreshed}</span>
            )}
            <button
              className="btn btn-outline btn-sm"
              onClick={fetchAll}
              disabled={loadingStats || loadingMetrics}
            >
              {loadingStats || loadingMetrics ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8, padding: '12px 16px', color: '#f87171', marginBottom: 20,
          }}>
            {error}
          </div>
        )}

        {/* Overall health + key pills */}
        {stats && (
          <div className="coach-section" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {overallHealth && (
                <span style={{
                  padding: '4px 16px', borderRadius: 999, fontWeight: 700, fontSize: 13,
                  background: overallHealth.color + '22',
                  border: `1px solid ${overallHealth.color}55`,
                  color: overallHealth.color,
                }}>
                  {overallHealth.label}
                </span>
              )}
              <Pill label="CAT Items" value={(collections.cat_item_bank ?? 0).toLocaleString()} color={statusColor(collections.cat_item_bank, 100, 10)} />
              <Pill label="Agent States" value={(collections.agent_mastery_states ?? 0).toLocaleString()} color={statusColor(collections.agent_mastery_states, 50, 10)} />
              <Pill label="FSRS Cards" value={(collections.fsrs_eval_cards ?? 0).toLocaleString()} color={statusColor(collections.fsrs_eval_cards, 10, 1)} />
              <Pill label="Learner BKT" value={(collections.learner_mastery_bkt ?? 0).toLocaleString()} color={statusColor(collections.learner_mastery_bkt, 100, 10)} />
              <Pill label="Agents Seeded" value={`${stats.total_agents_with_mastery ?? 0}/4`} color={statusColor(stats.total_agents_with_mastery, 4, 2)} />
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

          {/* Collection counts */}
          <div className="coach-section" style={{ margin: 0 }}>
            <SectionHeader title="Collection Seeding" subtitle="Document counts by AIL MongoDB collection" />
            {loadingStats ? (
              <div className="coach-loading">Loading…</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <CollectionRow name="cat_item_bank" count={collections.cat_item_bank ?? -1} goodAt={100} warnAt={10} />
                  <CollectionRow name="item_bank" count={collections.item_bank ?? -1} goodAt={50} warnAt={5} />
                  <CollectionRow name="agent_mastery_states" count={collections.agent_mastery_states ?? -1} goodAt={50} warnAt={10} />
                  <CollectionRow name="learner_mastery" count={collections.learner_mastery ?? -1} goodAt={10} warnAt={1} />
                  <CollectionRow name="learner_mastery_bkt" count={collections.learner_mastery_bkt ?? -1} goodAt={100} warnAt={10} />
                  <CollectionRow name="fsrs_eval_cards" count={collections.fsrs_eval_cards ?? -1} goodAt={10} warnAt={1} />
                  <CollectionRow name="ail_worker_log" count={collections.ail_worker_log ?? -1} goodAt={1} warnAt={0} />
                  <CollectionRow name="dpo_corpus" count={collections.dpo_corpus ?? -1} goodAt={10} warnAt={1} />
                </tbody>
              </table>
            )}
          </div>

          {/* Agent mastery per-agent */}
          <div className="coach-section" style={{ margin: 0 }}>
            <SectionHeader title="Agent Mastery Seeding" subtitle="Number of mastery state records per agent" />
            {loadingStats ? (
              <div className="coach-loading">Loading…</div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                  <tbody>
                    {Object.entries(agentMastery).map(([agent, count]) => (
                      <CollectionRow key={agent} name={agent} count={count} goodAt={3} warnAt={1} />
                    ))}
                  </tbody>
                </table>

                {/* Live mastery signal */}
                {metrics && (
                  <div style={{ paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Live Mastery Signal
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <Pill label="Avg p_mastery" value={liveAgent.avg_p_mastery?.toFixed(3) ?? '—'} color={liveAgent.avg_p_mastery > 0.3 ? '#4ade80' : '#fbbf24'} />
                      <Pill label="Total States" value={(liveAgent.total_states ?? 0).toLocaleString()} color={statusColor(liveAgent.total_states, 50, 10)} />
                    </div>
                    {liveAgent.tier_distribution && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Tier Distribution</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {Object.entries(liveAgent.tier_distribution).map(([tier, count]) => (
                            <span key={tier} style={{
                              padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                              background: 'rgba(99,102,241,0.12)', color: '#a78bfa',
                              border: '1px solid rgba(99,102,241,0.25)',
                            }}>
                              {tier}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* BKT Validation Metrics */}
        <div className="coach-section" style={{ marginBottom: 20 }}>
          <SectionHeader
            title="BKT Model Validation"
            subtitle="Accuracy and AUC-ROC measured against benchmark datasets. Run bootstrap scripts to seed real metrics."
          />
          {loadingMetrics ? (
            <div className="coach-loading">Loading metrics…</div>
          ) : Object.keys(benchmarks).length === 0 ? (
            <div className="coach-empty">
              <span className="coach-empty-icon">📊</span>
              <div className="coach-empty-title">No benchmark data yet</div>
              <div className="coach-empty-desc">Run <code>run_assist2009_bootstrap.py</code> on the server to seed validation metrics.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {Object.entries(benchmarks).map(([ds, data]) => (
                <div key={ds}>
                  <div style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace', marginBottom: 10 }}>
                    Dataset: <strong style={{ color: '#94a3b8' }}>{ds}</strong>
                    {data.seeded_at && <span style={{ marginLeft: 10, opacity: 0.6 }}>seeded {new Date(data.seeded_at).toLocaleDateString()}</span>}
                    <span style={{
                      marginLeft: 10, padding: '1px 8px', borderRadius: 999, fontSize: 10,
                      fontWeight: 700, textTransform: 'uppercase',
                      background: data.status === 'validated' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                      color: data.status === 'validated' ? '#4ade80' : '#fbbf24',
                    }}>
                      {data.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                    <MetricCard
                      title="Accuracy"
                      value={data.accuracy}
                      target={targets.accuracy}
                      unit="%"
                      note="Correct predictions / total"
                    />
                    <MetricCard
                      title="AUC-ROC"
                      value={data.auc_roc}
                      target={targets.auc_roc}
                      note="Area under ROC curve (1.0 = perfect)"
                    />
                    <MetricCard
                      title="Skills Validated"
                      value={data.n_skills_validated}
                      target={targets.n_skills_validated}
                      note="Distinct skills with enough data"
                    />
                    {data.calibration_error != null && (
                      <MetricCard
                        title="Calibration Error"
                        value={data.calibration_error}
                        note="Lower is better (ECE). < 0.05 = well-calibrated"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick reference */}
        <div style={{ fontSize: 12, color: '#475569', padding: '12px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <strong style={{ color: '#64748b' }}>Endpoints:</strong>
          {' '}
          <code style={{ color: '#94a3b8' }}>GET /scenario-os/ail/bootstrap/stats</code>
          {' · '}
          <code style={{ color: '#94a3b8' }}>GET /scenario-os/ail/bootstrap/validation-metrics</code>
          {' · '}
          <code style={{ color: '#94a3b8' }}>GET /scenario-os/ail/item-bank?domain=…</code>
          {' — platform-admin only'}
        </div>

      </div>
    </CoachLayout>
  );
}
